import { z } from 'zod';
import { ACCESS_ROLES, OFFICER_FLAGS } from '../constants/identity.constant';
import { createPaginationSchema, objectIdSchema } from './common.schema';
import { emailSchema, personNameSchema, phoneSchema } from './identity.schema';
import { orgUnitSummarySchema } from './orgUnit.schema';
import { rankSummarySchema } from './rank.schema';

/**
 * The officer master — and the ONLY user model in the system.
 *
 * An administrator is an officer carrying an `accessRole`; there is no separate admin
 * collection and no second login path. PA_REQUIREMENTS §1.3 principle 3 keeps rank
 * (what gets deployed) separate from access role (what grants dashboard access), and
 * this schema is where that separation is visible: `rankId` and `accessRole` are
 * unrelated fields and neither is derived from the other.
 */
export const officerBaseSchema = z.object({
  name: personNameSchema,
  /**
   * FR-5.8.1 — the primary identity: mandatory, globally unique, and the only
   * identifier every officer is guaranteed to have. Stored as exactly 10 digits,
   * because `phoneSchema` is a transform and parsing IS the normalisation.
   */
  phone: phoneSchema,
  /**
   * FR-5.8.2 — optional, unique WHEN PRESENT, via a partial index. The model omits the
   * field entirely rather than storing `null` or `''`; see `officer.model.ts`.
   */
  email: emailSchema.optional(),
  /**
   * Optional and NOT unique. Confirmed with the client: the phone number is the unique
   * identity, and collar/service numbers are not reliably distinct across units.
   * P1b's importer therefore keys idempotency on phone, not on this.
   */
  serviceNumber: z.string().trim().min(1).max(40).optional(),
  rankId: objectIdSchema,
  orgUnitId: objectIdSchema,
  photoUrl: z.string().trim().min(1).max(2_048).optional(),
});

export const createOfficerSchema = officerBaseSchema.extend({
  /** One field, not a set of booleans. `NONE` means officer-surface access only. */
  accessRole: z.enum(ACCESS_ROLES).default('NONE'),
  /** FR-3.1 — the first posting-history row's start date. Defaults to now. */
  postedFrom: z.iso.datetime().optional(),
});

/**
 * Note what is ABSENT: `orgUnitId` and `rankId`.
 *
 * Both go through `POST /officers/:id/transfer`, because FR-3.1 requires every change
 * of either to write a posting-history row. If `update` accepted them, the officer
 * record and their history would silently diverge — which is the entire reason
 * transfer is a separate endpoint rather than a flag on this one. zod strips them.
 */
export const updateOfficerSchema = z
  .object({
    name: personNameSchema,
    phone: phoneSchema,
    /**
     * `null` means CLEAR IT. The distinction matters: the model's setter turns a null
     * email into a no-op, so the service has to translate an explicit null into a real
     * `$unset`. An absent key and an explicit null are different requests.
     */
    email: emailSchema.nullable(),
    serviceNumber: z.string().trim().min(1).max(40).nullable(),
    accessRole: z.enum(ACCESS_ROLES),
    photoUrl: z.string().trim().min(1).max(2_048).nullable(),
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: 'At least one field must be provided' });

/** FR-3.1 — changes org unit and/or rank, and writes history, in one transaction. */
export const transferOfficerSchema = z.object({
  orgUnitId: objectIdSchema,
  /** Omit to keep the current rank — a posting is a place and a rank together. */
  rankId: objectIdSchema.optional(),
  effectiveFrom: z.iso.datetime().optional(),
});

export const officerSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  phone: z.string(),
  email: z.string().optional(),
  serviceNumber: z.string().optional(),
  accessRole: z.enum(ACCESS_ROLES),
  isActive: z.boolean(),
  photoUrl: z.string().optional(),
  rankId: objectIdSchema,
  orgUnitId: objectIdSchema,
  /**
   * Resolved by ONE batched lookup per page, never by `populate` and never per row.
   * `null` when the reference has been removed — a dangling id must render as a blank
   * cell, not as a crash on a list page.
   */
  rank: rankSummarySchema.nullable(),
  orgUnit: orgUnitSummarySchema.nullable(),
  /**
   * ⚠️ [C5c §11] THE UNIT THIS OFFICER'S POSTING ACTS AT, WHEN ITS UNIT IS ELEVATED — AND ABSENT
   * OTHERWISE. OPTIONAL, NOT NULLABLE, on [P6d]'s test: a reader that never receives it renders the
   * posting's level, so its absence changes no screen and no byte of an unelevated officer's payload.
   * A role LABEL reads `actingOrgUnit ?? orgUnit` (11g); the posting (`orgUnit`) never moves (11a).
   * ⚠️ ON THE ROW IT IS THE UNIT'S FACT, FOR EVERY ROLE — the drawer names each Dashboard-access
   * option for the level the unit acts at, so promoting a constable there reads "HQ Admin" before the
   * save. The IDENTITY below carries it for the three nodal roles only.
   */
  actingOrgUnit: orgUnitSummarySchema.optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

/** Every value here has a supporting index on the model (CLAUDE.md §7). */
export const OFFICER_SORT_FIELDS = ['name', 'serviceNumber', 'createdAt', 'updatedAt'] as const;
export type OfficerSortField = (typeof OFFICER_SORT_FIELDS)[number];

export const listOfficersQuerySchema = createPaginationSchema(OFFICER_SORT_FIELDS, 'name')
  .extend({
    /**
     * NARROWS the caller's scope and can never widen it — the scope filter and this
     * one are ANDed. Asking for a sibling division returns nothing, which is the same
     * answer as asking for a unit that does not exist (FR-3.6).
     */
    orgUnitId: objectIdSchema.optional(),
    /** false = this unit only; true = this unit and everything beneath it. */
    includeDescendants: z.stringbool().default(true),
    rankId: objectIdSchema.optional(),
    /**
     * ⚠️ [P4-UX.2] A COMMA-SEPARATED ALLOW-LIST, FOR A COMMAND ROLE'S ELIGIBLE RANKS.
     *
     * A query string carries strings, so it arrives as `rankIds=a,b,c` and is split here —
     * the ONE place that decides the separator, rather than at each caller. Bounded at 40,
     * the same ceiling the policy schema puts on the allow-list it comes from, so a URL
     * cannot ask for an unbounded `$in`.
     *
     * Separate from `rankId` rather than replacing it: `rankId` is "officers of this rank",
     * a different question with a different index, and its existing callers are unaffected.
     */
    rankIds: z
      .string()
      .trim()
      .min(1)
      .max(1_200)
      .transform((raw) =>
        raw
          .split(',')
          .map((id) => id.trim())
          .filter((id) => id.length > 0),
      )
      .pipe(z.array(objectIdSchema).min(1).max(40))
      .optional(),
    accessRole: z.enum(ACCESS_ROLES).optional(),
    isActive: z.stringbool().optional(),
    search: z.string().trim().min(1).max(160).optional(),
  })
  .extend({ sortOrder: z.enum(['asc', 'desc']).default('asc') });

/** What `/auth/me` returns, and what rides inside a session payload. */
export const officerIdentitySchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  phone: z.string(),
  email: z.string().optional(),
  accessRole: z.enum(ACCESS_ROLES),
  orgUnitId: objectIdSchema,
  rankId: objectIdSchema,
  rank: rankSummarySchema.nullable(),
  orgUnit: orgUnitSummarySchema.nullable(),
  /**
   * ⚠️ [C5c §11] THE UNIT THIS OFFICER'S POSTING ACTS AT, WHEN ITS UNIT IS ELEVATED — AND ABSENT
   * OTHERWISE. OPTIONAL, NOT NULLABLE, on [P6d]'s test: a reader that never receives it renders the
   * posting's level, so its absence changes no screen and no byte of an unelevated officer's payload.
   * A role LABEL reads `actingOrgUnit ?? orgUnit` (11g); the posting (`orgUnit`) never moves (11a).
   * ⚠️ HERE IT IS WHERE THE CALLER ACTS, SO ONLY THE THREE NODAL ROLES CARRY IT — the same
   * `elevationAppliesTo` the scope resolver reads. A constable's and a super admin's identity never
   * gain the key (`officer-contract.md`); the officer ROW above carries it for every role.
   */
  actingOrgUnit: orgUnitSummarySchema.optional(),
  /**
   * ⚠️ [amended P5b] NULLABLE, NOT OPTIONAL — AND THE DIFFERENCE IS NOT COSMETIC HERE.
   *
   * The officer app's profile screen renders a labelled row for every one of these, so the
   * question it asks is "what goes in the box", and `null` answers it — a dash. `optional`
   * answers a different question ("was this sent?") that no screen on either surface has any
   * way to act on, and it is the shape `orgUnitSummarySchema`'s header refuses for exactly
   * that reason. `rank` and `orgUnit` above are nullable for the same reason and set the
   * precedent.
   *
   * ⚠️ AND DIVERGENCE #100 DOES NOT BITE, though it looks as though it should. That rule
   * governs `orgUnitSummarySchema`, which is unchanged: its cost is that TWO services build
   * it by hand behind narrowed projections, so an optional field there renders inconsistently.
   * THIS schema has exactly one builder — `toOfficerIdentityDto` — reading a full document,
   * so there is no projection for a field to drift behind. Check that claim is still true
   * before adding a third field.
   *
   * `service_number` is optional and non-unique on the model (client-confirmed: the phone
   * number is the identity), so an officer genuinely may not have one.
   */
  serviceNumber: z.string().nullable(),
  /**
   * ⚠️ [amended P5b] THE DIVISION, DERIVED FROM `ancestors` — NEVER WALKED PER REQUEST.
   *
   * `orgUnit` above is the officer's POSTING — a unit, "Bowbazar PS". The division is its
   * ancestor, and the officer app's profile shows both. Three things about the derivation
   * are written at the resolver (`officerLookup.service.ts`) and repeated here because this
   * is where a reader meets the field:
   *
   *   • an officer posted to a DIVISION node has no DIVISION ancestor, and the answer is
   *     their own unit rather than `null`;
   *   • `depth` is never a proxy for `type` — the tree is dynamic-depth and both
   *     `orgUnit.model.ts` and `identity.constant.ts` say nothing derives one from the other;
   *   • `null` is a real answer. Seed roots, `seed:demo` units and anything created through
   *     `POST /org-units` need not sit under a division at all.
   */
  division: orgUnitSummarySchema.nullable(),
  /**
   * ⚠️ [C5b §5] THE OFFICER'S OWN FLAGS — GRANTS ON TOP OF `accessRole`, NEVER DENIALS.
   *
   * A REQUIRED key, `[]` when there are none, for `serviceNumber`'s reason: the web decides
   * what to show from it (`meetsRequirement`), and "the server did not say" must not be a third
   * answer beside "none" and "these". The server filters it through `knownFlags`, so a value
   * nobody declared never reaches a client; the stored column carries no enum (see
   * `identity.constant.ts` for why).
   *
   * ⚠️ IT IS ON THE READ AND ON NOTHING THAT WRITES. `createOfficerSchema` and
   * `updateOfficerSchema` above do not declare it, so zod strips it from any request — no API
   * route can grant or revoke a flag, which is what lets the API's authorisation read it.
   *
   * The one-builder claim above still holds: `toOfficerIdentityDto` is the only builder, and it
   * reads a full document.
   */
  flags: z.array(z.enum(OFFICER_FLAGS)),
});

/**
 * [P3a] An officer as one row in a picker or on an order.
 *
 * ⚠️ ONE SHAPE, BECAUSE PICKING THE WRONG SEN OUT OF FORTY IS THE LIKELIEST DATA-ENTRY
 * ERROR IN THE SYSTEM (PA_REQUIREMENTS §6.1 Step 2). Everything a human needs to tell two
 * officers of the same name apart — rank, where they are posted, a phone number, a face —
 * travels together, so no consumer can accidentally render a bare name and no consumer has
 * to assemble the row itself.
 *
 * Deliberately not `officerIdentitySchema`: that one answers "who am I" and carries an
 * access role, which has no business on a printed order.
 */
export const officerSummarySchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  phone: z.string(),
  rankShortName: z.string().nullable(),
  orgUnitName: z.string().nullable(),
  /**
   * ⚠️ [amended PRE-P6.1] WHERE THIS OFFICER SITS IN THE TREE, ROOT FIRST, INCLUDING THEIR OWN
   * UNIT — `['Kolkata Police', 'Central Division', 'Bowbazar PS']`.
   *
   * `orgUnitName` above is the leaf and answers "which station"; a nodal reading a chip on the
   * fill screen is asking the other question — "whose people are these" — and the division is
   * the part of the answer that is missing from a station's name.
   *
   * ⚠️ AN ARRAY, NOT A JOINED STRING. The separator is a rendering decision and the two
   * surfaces that show this may not make it the same way; `OrgUnitDetail`'s Lineage tab
   * already indents the same data rather than joining it.
   *
   * ⚠️ REQUIRED KEY, EMPTY ARRAY WHEN UNRESOLVABLE — never `optional`. `[]` is a statement
   * ([WINGS]'s `womenPoliceStations` argument) and the required key is what makes a new
   * builder a compile error rather than a field that silently stops arriving; divergence #100
   * is the standing warning about the other choice. There is exactly ONE builder,
   * `loadOfficerSummaries` — check that is still true before adding a second field.
   */
  orgUnitPath: z.array(z.string()),
  photoUrl: z.string().nullable(),
});
